const BOOT_LOADER_SELECTOR = "[data-app-boot-loader]";
const BOOT_CORE_IMAGE_SELECTOR = "[data-boot-core-image]";
const ROOT_ID = "root";
const EXIT_DURATION_MS = 120;
const SCENE_ASSET_FALLBACK_MS = 1_200;

let releaseScheduled = false;
let scenePreparationStarted = false;
let cancelScenePreparation: (() => void) | undefined;

/**
 * Shows the loading logo once decoded. This optional artwork never holds up
 * the ready application.
 */
export function prepareBootLoaderScene(): void {
  if (releaseScheduled || scenePreparationStarted || typeof document === "undefined") return;

  const loader = document.querySelector<HTMLElement>(BOOT_LOADER_SELECTOR);
  if (!loader) return;
  const bootLoader = loader;

  scenePreparationStarted = true;
  const image = bootLoader.querySelector<HTMLImageElement>(BOOT_CORE_IMAGE_SELECTOR);
  let started = false;
  const fallback = window.setTimeout(markCoreUnavailableAndStart, SCENE_ASSET_FALLBACK_MS);
  cancelScenePreparation = () => {
    window.clearTimeout(fallback);
    image?.removeEventListener("load", decodeAndStart);
    image?.removeEventListener("error", markCoreUnavailableAndStart);
    cancelScenePreparation = undefined;
  };

  function startScene(): void {
    if (started || releaseScheduled) return;
    started = true;
    cancelScenePreparation?.();
    window.requestAnimationFrame(() => {
      if (releaseScheduled || !bootLoader.isConnected) return;
      bootLoader.classList.add("is-scene-ready");
    });
  }

  function markCoreUnavailableAndStart(): void {
    if (releaseScheduled) return;
    bootLoader.classList.add("is-core-unavailable");
    startScene();
  }

  function decodeAndStart(): void {
    if (typeof image?.decode !== "function") {
      startScene();
      return;
    }
    void image.decode().then(startScene, markCoreUnavailableAndStart);
  }

  if (!image || (image.complete && image.naturalWidth === 0)) {
    markCoreUnavailableAndStart();
  } else if (image.complete) {
    decodeAndStart();
  } else {
    image.addEventListener("load", decodeAndStart, { once: true });
    image.addEventListener("error", markCoreUnavailableAndStart, { once: true });
  }
}

export function showBootError(error: unknown): void {
  if (typeof document === "undefined") return;

  const loader = document.querySelector<HTMLElement>(BOOT_LOADER_SELECTOR);
  if (!loader) {
    revealRoot(document.getElementById(ROOT_ID));
    return;
  }

  releaseScheduled = true;
  cancelScenePreparation?.();
  loader.classList.add("is-scene-ready", "is-boot-error", "is-core-unavailable");
  loader.setAttribute("aria-busy", "false");
  loader.setAttribute("role", "alert");
  const message = loader.querySelector<HTMLElement>("[data-boot-message]");
  if (message) message.textContent = "Не удалось открыть приложение. Перезагрузите и попробуйте снова.";
  console.error("Application bootstrap failed", error);
}

/**
 * Keeps the server-rendered startup loader mounted across React bootstrap,
 * lazy chunk loading and the first data request. It is removed only after
 * the destination screen has committed, so intermediate fallbacks never
 * become visible or restart the loading animation.
 */
export function releaseBootLoaderAfterPaint(): void {
  if (releaseScheduled || typeof document === "undefined") return;

  const loader = document.querySelector<HTMLElement>(BOOT_LOADER_SELECTOR);
  const root = document.getElementById(ROOT_ID);
  if (!loader) {
    revealRoot(root);
    return;
  }

  releaseScheduled = true;
  cancelScenePreparation?.();
  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      revealRoot(root);
      loader.classList.add("is-leaving");
      if (reducedMotion) loader.remove();
      else window.setTimeout(() => loader.remove(), EXIT_DURATION_MS);
    });
  });
}

/**
 * Makes a render-error recovery screen reachable even when React fails before
 * the normal application bootstrap can schedule the animated loader exit.
 */
export function releaseBootLoaderImmediately(): void {
  if (typeof document === "undefined") return;
  releaseScheduled = true;
  cancelScenePreparation?.();
  revealRoot(document.getElementById(ROOT_ID));
  document.querySelector<HTMLElement>(BOOT_LOADER_SELECTOR)?.remove();
}

function revealRoot(root: HTMLElement | null): void {
  root?.removeAttribute("aria-hidden");
  root?.removeAttribute("inert");
}
